import { useEffect, useState } from 'react';

import {
  fetchPublishedChangeLogEntries,
  subscribeToPublishedChangeLogEntries,
  type ChangeLogEntry,
} from './changelog-service';

type ChangeLogState = {
  entries: ChangeLogEntry[];
  error: Error | null;
  loading: boolean;
};

export function usePublishedChangeLogEntries(limitCount = 20) {
  const [state, setState] = useState<ChangeLogState>({
    entries: [],
    error: null,
    loading: true,
  });

  useEffect(() => {
    let active = true;

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: currentState.entries.length === 0,
    }));

    const unsubscribe = subscribeToPublishedChangeLogEntries({
      limitCount,
      onError: (error) => {
        if (!active) {
          return;
        }

        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
      },
      onNext: (entries) => {
        if (!active) {
          return;
        }

        setState({ entries, error: null, loading: false });
      },
    });

    fetchPublishedChangeLogEntries(limitCount)
      .then((entries) => {
        if (!active) {
          return;
        }

        setState((currentState) => {
          if (entries.length === 0 && currentState.entries.length > 0) {
            return currentState;
          }

          return { entries, error: null, loading: false };
        });
      })
      .catch((error: Error) => {
        if (!active) {
          return;
        }

        setState((currentState) => {
          if (currentState.entries.length > 0) {
            return currentState;
          }

          return {
            ...currentState,
            error,
            loading: false,
          };
        });
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [limitCount]);

  return state;
}
